<template>
	<div class="zn-ads-program">
		<div class="zn-ads-program-title">Ads program</div>
		<div class="zn-ads-program-desc">You can configure ads program for the shops here.</div>

		<div class="zn-section">
			<div class="zn-section-title">Configuration</div>

			<div class="zn-ads-program-metrics">
				<div class="zn-ads-program-metric" v-for="(metric, index) in configurationMetrics" :key="index" @click="viewingEvents = {title: metric.label, events: metric.events}">
					<div class="zn-metric-label">{{ metric.label }}</div>
					<div class="zn-metric-value">{{ metric.value }}</div>
				</div>
			</div>

			<div class="zn-ads-program-actions">
				<button class="zn-ads-program-action" @click="changeEligibilityAll(true)">Make all eligible</button>
				<button class="zn-ads-program-action" @click="changeEligibilityAll(false)">Make all ineligible</button>
				<button class="zn-ads-program-action" @click="configureDefaultEligibility">Configure default eligibility</button>
			</div>
		</div>

		<div class="zn-section">
			<div class="zn-section-title">Analytics</div>

			<div class="zn-date-range-picker">
				<DateRangePicker
					opens="right"
					v-model="selectedDates"
				/>
			</div>

			<div class="zn-ads-program-metrics">
				<div class="zn-ads-program-metric" v-for="(metric, index) in analyticsMetrics" :key="index" @click="viewingEvents = {title: metric.label, events: metric.events}">
					<div class="zn-metric-label">{{ metric.label }}</div>
					<div class="zn-metric-value">{{ metric.value }}</div>
				</div>
			</div>
		</div>

		<EventsViewer
			v-if="viewingEvents"
			:title="viewingEvents.title"
			:events="viewingEvents.events"
			@close="viewingEvents = null; $emit('resetViewingShop')"
		/>
	</div>
</template>


<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import API_URL from '@/utils/sourceApiUrl'
import request from '@/utils/request.js'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import EventsViewer from './EventsViewer.vue'

const now = new Date()

export default {
	components: {
		DateRangePicker,
		EventsViewer,
	},
	props: {
		viewingShop: Number
	},
	data() {
		return {
			events: null,
			selectedDates: {
				startDate: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
  			endDate: now,
			},
			viewingEvents: null,
		}
	},
  computed: {
    ...mapState({
      sourceShops: state => state.shops.listShops,
      platform: state => state.platform,
    }),
    adsEligibleShops() {
      return this.sourceShops[this.platform].filter(shop => shop.adsProgramEligible && !shop.deletedAt && !shop.frozen)
    },
    adsEnabledShops() {
      return this.sourceShops[this.platform].filter(shop => shop.adsProgramEligible && shop.data?.adsProgramEnabled && !shop.deletedAt && !shop.frozen)
    },
		filteredEvents() {
			if (!this.events) return []

			const start = this.dateStart(this.selectedDates.startDate)
			const end = this.dateEnd(this.selectedDates.endDate)

			return this.events.filter(event => {
				const eventDate = new Date(event.date)
				return eventDate >= start && eventDate <= end
			})
		},
		modalViewsEvents() {
			return this.filteredEvents
									.filter(event => event.name === 'modalView')
		},
		modalDismissalsEvents() {
			return this.filteredEvents
									.filter(event => event.name === 'dismiss')
		},
		modalPermanentDismissalsEvents() {
			return this.filteredEvents
									.filter(event => event.name === 'permanentDismiss')
		},
		modalClicksEvents() {
			return this.filteredEvents
									.filter(event => event.name === 'click')
		},
		pageViewsEvents() {
			return this.filteredEvents
									.filter(event => event.name === 'pageView')
		},
		ctaClicksEvents() {			
			return this.filteredEvents
									.filter(event => event.name === 'ctaClick')
		},
		configurationMetrics() {
			return [
				{
					label: 'Eligible shops',
					value: this.adsEligibleShops.length,
					events: this.adsEligibleShops.map(shop => {
						return {
							shop,
						}
					})
				},
				{
					label: 'Enabled shops',
					value: this.adsEnabledShops.length,
					events: this.adsEnabledShops.map(shop => {
						return {
							shop,
						}
					})
				}
			]
		},
		analyticsMetrics() {
			return [
				{
					label: 'Modal views',
					value: this.modalViewsEvents.reduce((acc, event) => acc + Number(event.value), 0),
					events: this.modalViewsEvents.map(event => {
						const shopId = event.shopId
						const shop = this.sourceShops[this.platform].find(shop => shop.id === shopId)
						return {
							shop,
							date: event.date,
						}
					})

				},
				{
					label: 'Dismissals',
					value: this.modalDismissalsEvents.reduce((acc, event) => acc + Number(event.value), 0),
					events: this.modalDismissalsEvents.map(event => {
						const shopId = event.shopId
						const shop = this.sourceShops[this.platform].find(shop => shop.id === shopId)
						return {
							shop,
							date: event.date,
						}
					})
				},
				{
					label: 'Permanent dismissals',
					value: this.modalPermanentDismissalsEvents.reduce((acc, event) => acc + Number(event.value), 0),
					events: this.modalPermanentDismissalsEvents.map(event => {
						const shopId = event.shopId
						const shop = this.sourceShops[this.platform].find(shop => shop.id === shopId)
						return {
							shop,
							date: event.date,
						}
					})
				},
				{
					label: 'Modal CTA clicks',
					value: this.modalClicksEvents.reduce((acc, event) => acc + Number(event.value), 0),
					events: this.modalClicksEvents.map(event => {
						const shopId = event.shopId
						const shop = this.sourceShops[this.platform].find(shop => shop.id === shopId)
						return {
							shop,
							date: event.date,
						}
					})
				},
				{
					label: 'Page views',
					value: this.pageViewsEvents.reduce((acc, event) => acc + Number(event.value), 0),
					events: this.pageViewsEvents.map(event => {
						const shopId = event.shopId
						const shop = this.sourceShops[this.platform].find(shop => shop.id === shopId)
						return {
							shop,
							date: event.date,
						}
					})
				},
				{
					label: 'Page CTA clicks',
					value: this.ctaClicksEvents.reduce((acc, event) => acc + Number(event.value), 0),
					events: this.ctaClicksEvents.map(event => {
						const shopId = event.shopId
						const shop = this.sourceShops[this.platform].find(shop => shop.id === shopId)
						return {
							shop,
							date: event.date,
						}
					})
				}
			]
		}
  },
	async mounted() {
		if (!this.events) {
			const { events } = await request.get(`${API_URL[this.platform]}/admin/events`, { extension: 'ads-program' })
			this.events = events
		}
	},
  methods: {
    ...mapActions({
      toggleAllAdsProgram: 'shops/toggleAllAdsProgram',
      setConfigs: 'configs/setConfigs',
    }),
    ...mapMutations({
      setShopsAdsProgramEligible: 'shops/setShopsAdsProgramEligible',
    }),
    changeEligibilityAll(eligible) {
      this.$store.commit('confirm', {
        title: 'Change eligibility for all shops',
        message: `Are you sure to make all shops ${eligible ? 'eligible' : 'ineligible'}?`,
        status: 'danger',
        confirmHandler: async () => {
          try {
            await this.toggleAllAdsProgram({ eligible })
            this.$store.commit('notify', 'Ads Program updated')
            this.setShopsAdsProgramEligible({ platform: this.platform, adsProgramEligible: eligible })
          } catch (err) {
            console.log('Warn 1: ', err)
            this.$store.commit('notify', 'Something went wrong. Please try again.')
          }
        }
      })
    },
    configureDefaultEligibility() {
      this.$store.commit('prompt', {
        title: 'Configure default eligibility',
        message: 'Default eligibility for new shops',
        type: 'switch',
        defaultValue: [this.$store.state.configs.configs['defaultAdsProgramEligible'] === 'true'],
        confirmHandler: async (eligible) => {
          try {
            await this.setConfigs({ defaultAdsProgramEligible: String(eligible) })
            this.$store.commit('notify', 'Ads Program updated')
          } catch (err) {
            console.log('Warn 1: ', err)
            this.$store.commit('notify', 'Something went wrong. Please try again.')
          }
        }
      })
    },
		dateStart(date) {
			const adjustedDate = new Date(date)
			adjustedDate.setHours(0, 0, 0, 0)
			return adjustedDate
		},
		dateEnd(date) {
			const adjustedDate = new Date(date)
			adjustedDate.setHours(23, 59, 59, 999)
			return adjustedDate
		}
  },
	watch: {
		viewingShop() {
			if (this.viewingShop) {
				if (!this.events) return

				const shop = this.sourceShops[this.platform].find(shop => shop.id === this.viewingShop)

				this.viewingEvents = {
					title: `Events for ${shop.name}`,
					events: this.events.filter(event => {
						return event.shopId === this.viewingShop
					}).map(event => ({
						shopId: event.shopId,
						shopName: event.name,
						date: event.date,
					}))
				}
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.zn-ads-program {
	margin-top: 64px;

	.zn-ads-program-title {
		font-weight: 500;
		font-size: 22px;
	}

	.zn-ads-program-desc {
		font-size: 14px;
		margin-top: 8px;
		color: $fade;
	}

	.zn-section {
		margin: 32px 0;

		.zn-section-title {
			margin-bottom: 24px;
			font-weight: 500;
			font-size: 18px;
		}

		.zn-date-range-picker {
			margin-bottom: 8px;
		}

		.zn-ads-program-metrics {
			display: flex;
			flex-wrap: wrap;
			gap: 16px;

			.zn-ads-program-metric {
				background-color: white;
				border: 1px solid $light;
				border-radius: 5px;
				padding: 16px;
				flex-basis: calc(33.33% - 11px);
				cursor: pointer;

				&:hover {
					background-color: rgba($light, 0.5);
				}

				.zn-metric-value {
					font-size: 32px;
					margin-top: 12px;
					font-size: bold;
				}
			}
		}

		.zn-ads-program-actions {
			display: flex;
			gap: 12px;
			margin-top: 24px;

			.zn-ads-program-action {
				background-color: $primary;
				color: white;
				padding: 12px 16px;
				border: 1px solid $fade;
				border-radius: 5px;
				font-size: 14px;
				font-weight: 500;
				cursor: pointer;
				transition: background-color 0.5s;

				&:hover {
					opacity: 0.9;
				}
			}
		}
	}

}
</style>